import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getOpenBalances,
  MentorOpenBalance,
  MentorPayoutsKeys,
} from "@/features/payouts/api/mentor-payouts";
import MentorPaymentsDialog from "@/features/payouts/components/MentorPaymentsDialog";

const formatCurrencyAmount = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const OpenPayoutsTable = () => {
  const [selectedMentor, setSelectedMentor] =
    useState<MentorOpenBalance | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: [MentorPayoutsKeys.GET_OPEN_BALANCES],
    queryFn: getOpenBalances,
  });

  if (isLoading) {
    return (
      <Card className="p-8 flex items-center justify-center">
        <LoadingSpinner />
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Open Payouts</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mentor Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Open Balance</TableHead>
                <TableHead className="text-right">Available Payments</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center text-muted-foreground"
                  >
                    No open payouts found
                  </TableCell>
                </TableRow>
              ) : (
                data.map((balance) => (
                  <TableRow
                    key={balance.mentorId}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setSelectedMentor(balance)}
                  >
                    <TableCell className="font-medium">
                      {balance.mentorName}
                    </TableCell>
                    <TableCell>{balance.mentorEmail}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrencyAmount(balance.openBalanceUsd)}
                    </TableCell>
                    <TableCell className="text-right">
                      {balance.availablePaymentsCount}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <MentorPaymentsDialog
        mentor={selectedMentor}
        onClose={() => setSelectedMentor(null)}
      />
    </>
  );
};

export default OpenPayoutsTable;
